import { useMemo } from 'react';
import { type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type TFunction } from 'i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { ServerOff } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@bliks-io/ui';
import { ErrorCode, type MigrateShadowSystemRequest, type ShadowSystem } from '@bliks-io/contracts';
import {
  FormInput,
  FormTextarea,
  FormSystemCategorySelect,
  FormSystemSelect,
  FormUserSelect
} from '@/components/form/rhf';
import { FormDialog } from '@/components/dialogs/FormDialog';
import { useMigrateShadowSystem } from '@/api/mutations/useMigrateShadowSystem';

/**
 * Flat form schema kept deliberately separate from the discriminated API
 * request: a single object with a `mode` discriminator plays nicely with
 * `zodResolver`, and `superRefine` enforces the per-mode required fields.
 * `onSubmit` maps it to the discriminated `MigrateShadowSystemRequest`.
 */
const ResolveFormSchema = z
  .object({
    mode: z.enum(['existing', 'new']),
    targetSystemId: z.string().optional(),
    name: z.string().optional(),
    systemOwner: z.string().optional(),
    category: z.string().optional(),
    url: z.string().optional(),
    description: z.string().optional()
  })
  .superRefine((val, ctx) => {
    if (val.mode === 'existing') {
      if (!val.targetSystemId) {
        ctx.addIssue({
          code: 'custom',
          path: ['targetSystemId'],
          message: ErrorCode.FIELD_REQUIRED
        });
      }
      return;
    }
    if (!val.name?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['name'],
        message: ErrorCode.FIELD_REQUIRED
      });
    }
    if (!val.systemOwner) {
      ctx.addIssue({
        code: 'custom',
        path: ['systemOwner'],
        message: ErrorCode.FIELD_REQUIRED
      });
    }
  });

type ResolveFormValues = z.infer<typeof ResolveFormSchema>;

/**
 * Fields for promoting a shadow to a brand-new System. Mirrors the add-system
 * payload (name/owner/category/url/description) but with shadow-scoped copy, so
 * it is deliberately not shared with SystemFormDialog (which also owns logo
 * upload, licence seats, and edit-mode dirty handling).
 */
function NewSystemFields({ control, t }: { control: Control<ResolveFormValues>; t: TFunction }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormInput
          control={control}
          name="name"
          label={t('shadowSystems.resolveDialog.name')}
          required
        />
        <FormUserSelect
          control={control}
          name="systemOwner"
          label={t('shadowSystems.resolveDialog.systemOwner')}
          placeholder={t('shadowSystems.resolveDialog.selectOwner')}
          required
        />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormSystemCategorySelect
          control={control}
          name="category"
          label={t('shadowSystems.resolveDialog.category')}
        />
        <FormInput control={control} name="url" label={t('shadowSystems.resolveDialog.url')} />
      </div>
      <FormTextarea
        control={control}
        name="description"
        label={t('shadowSystems.resolveDialog.description')}
        rows={3}
      />
    </div>
  );
}

interface ShadowResolveDialogProps {
  open: boolean;
  shadowSystem: ShadowSystem | null;
  onOpenChange: (open: boolean) => void;
}

export function ShadowResolveDialog({
  open,
  shadowSystem,
  onOpenChange
}: ShadowResolveDialogProps) {
  const { t } = useTranslation();
  const migrate = useMigrateShadowSystem(shadowSystem?._id ?? '');

  // New-System text fields are prefilled from the shadow's payload; the owner
  // is left empty so the org-manager picks it explicitly every time.
  const defaultValues = useMemo<ResolveFormValues>(
    () => ({
      mode: 'existing',
      targetSystemId: '',
      name: shadowSystem?.payload.name ?? '',
      systemOwner: '',
      category: shadowSystem?.payload.category ?? '',
      url: shadowSystem?.payload.url ?? '',
      description: shadowSystem?.payload.description ?? ''
    }),
    [shadowSystem]
  );

  const handleSubmit = async (values: ResolveFormValues) => {
    if (!shadowSystem) return;

    // superRefine guarantees the per-mode required fields are present; narrow
    // explicitly so the request never carries a fabricated empty string.
    let request: MigrateShadowSystemRequest;
    if (values.mode === 'existing') {
      if (!values.targetSystemId) return;
      request = { mode: 'existing', targetSystemId: values.targetSystemId };
    } else {
      const name = values.name?.trim();
      if (!name || !values.systemOwner) return;
      request = {
        mode: 'new',
        system: {
          name,
          systemOwner: values.systemOwner,
          category: values.category?.trim() || undefined,
          url: values.url?.trim() || undefined,
          description: values.description?.trim() || undefined
        }
      };
    }

    await migrate.mutateAsync(request);
    toast.success(t('shadowSystems.actions.resolved'));
    onOpenChange(false);
  };

  return (
    <FormDialog<ResolveFormValues>
      open={open}
      onOpenChange={onOpenChange}
      title={t('shadowSystems.resolveDialog.title')}
      description={t('shadowSystems.resolveDialog.description')}
      icon={ServerOff}
      schema={ResolveFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      submitLabel={t('shadowSystems.actions.resolve')}
      size="lg"
    >
      {({ form }) => {
        const mode = form.watch('mode');
        return (
          <div className="flex flex-col gap-6">
            <Tabs
              value={mode}
              onValueChange={(v) =>
                form.setValue('mode', v as ResolveFormValues['mode'], {
                  shouldDirty: true
                })
              }
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="existing">
                  {t('shadowSystems.resolveDialog.useExisting')}
                </TabsTrigger>
                <TabsTrigger value="new">{t('shadowSystems.resolveDialog.createNew')}</TabsTrigger>
              </TabsList>
            </Tabs>

            {mode === 'existing' ? (
              <FormSystemSelect
                control={form.control}
                name="targetSystemId"
                label={t('shadowSystems.resolveDialog.targetSystem')}
                placeholder={t('shadowSystems.resolveDialog.selectSystem')}
                required
              />
            ) : (
              <NewSystemFields control={form.control} t={t} />
            )}
          </div>
        );
      }}
    </FormDialog>
  );
}
